import * as React from 'react';
import { ArticleFragmentContext } from '../contexts';
import { IRelatedArticles, IArticleFragment } from '../types';
import useRelatedArticles from '../hooks/useRelatedArticles';

type GroupFunction = (data?: IRelatedArticles) => React.ReactNode;

const isGroupFunction = (arg: any): arg is GroupFunction => {
  return typeof arg === 'function';
};

function returnChild(
  children: GroupFunction | React.ReactNode,
  data: IRelatedArticles | undefined
) {
  return (
    <>
      {React.isValidElement(children)
        ? children
        : isGroupFunction(children)
        ? children(data)
        : null}
    </>
  );
}

type GroupProps = {
  children: GroupFunction | React.ReactNode;
};

export const Group: React.FC<GroupProps> = ({ children }) => {
  const relatedArticlesData = useRelatedArticles();
  return <>{returnChild(children, relatedArticlesData)}</>;
};

type ArticleLoopData = {
  index: number;
  article: IArticleFragment;
};
type ArticleLoopFunction = (data: ArticleLoopData) => React.ReactNode;
const isArticleLoopFunction = (arg: any): arg is ArticleLoopFunction => {
  return typeof arg === 'function';
};

/** Checks the type of the react child and returns in the correct way */
export function returnLoopChild(
  children: ArticleLoopFunction | React.ReactNode,
  data: ArticleLoopData
) {
  return React.isValidElement(children)
    ? children
    : isArticleLoopFunction(children)
    ? children(data)
    : null;
}

type LoopArticlesProps = {
  children: React.ReactNode | GroupFunction;
};
/**
 * For each of the related articles it repeats the children passed in.
 * So if you pass a render prop as a child it will be called with each sub category result.
 * Else if you pass in React Elements they will be repeated for each sub category result.
 */
export const Loop = ({ children }: LoopArticlesProps) => {
  const relatedArticles = useRelatedArticles();
  if (!relatedArticles) return null;

  return (
    <>
      {relatedArticles.map((article, index) => (
        <ArticleFragmentContext.Provider key={article.id} value={article}>
          {returnLoopChild(children, {
            article,
            index,
          })}
        </ArticleFragmentContext.Provider>
      ))}
    </>
  );
};

type CountProps = {
  children?: (data: { count: number }) => React.ReactNode;
};
/**
 * Either prints out or passes (in the case of a render prop as children) the
 * total number of related articles.
 */
export const Count = ({ children }: CountProps) => {
  const relatedArticles = useRelatedArticles();
  if (!relatedArticles) return null;
  const count = relatedArticles.length;

  if (children) return <>{children({ count })}</>;
  return <>{count}</>;
};
